import { useQuery } from '@tanstack/react-query';
import { configControllerGetPublicProductConfigQueryOptions } from 'korint-sdk';
import { useMemo } from 'react';

import {
  hasNoStopDateInput,
  isStopReasonAvailable,
  type StopReasonPolicyDates,
} from './stop-reasons';

/**
 * Derives, from a product's public config, the stop reasons that need special termination UI:
 * - `fullRefundReasons`: reasons that void the policy from its start date — the date picker is
 *   hidden and "now" is sent, since the backend ignores any chosen date.
 * - `hasNoStopDateReasons`: reasons that take no stopDate input at all (a requestNoticePeriod with
 *   no appliedAtDate) — the date picker is hidden, since the backend computes the effective date
 *   itself (the policy's next anniversary).
 * - `unavailableReasons`: reasons whose validity window or notice-period deadline has elapsed, or
 *   whose notice-period anchor the policy cannot supply — shown disabled in the list, since the
 *   backend would reject them.
 */
export const usePolicyStopReasons = ({
  productId,
  policy,
}: {
  productId: string;
  policy: StopReasonPolicyDates;
}) => {
  const { data: stopReasonConfigs = [], isPending } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId),
    select: ({ policyStopReasons }) => policyStopReasons ?? [],
  });

  // Destructured so the memo keys off primitives — callers pass an inline policy literal.
  const { startedAt, nextRenewalAt, suspendedAt } = policy;

  return useMemo(() => {
    const policyDates = { startedAt, nextRenewalAt, suspendedAt };
    const fullRefundReasons = new Set<string>();
    const hasNoStopDateReasons = new Set<string>();
    const unavailableReasons = new Set<string>();
    for (const reason of stopReasonConfigs) {
      if (reason.refundType === 'FULL') {
        fullRefundReasons.add(reason.reason);
      }
      if (hasNoStopDateInput(reason)) {
        hasNoStopDateReasons.add(reason.reason);
      }
      if (!isStopReasonAvailable({ reason, policy: policyDates })) {
        unavailableReasons.add(reason.reason);
      }
    }
    // isPending, not the empty list: callers routing on a reason's config must wait for it rather
    // than read "no reasons yet" as "this reason needs nothing".
    return {
      stopReasonConfigs,
      fullRefundReasons,
      hasNoStopDateReasons,
      unavailableReasons,
      isPending,
    };
  }, [stopReasonConfigs, startedAt, nextRenewalAt, suspendedAt, isPending]);
};
